import { type BridgeData } from '@databases/database-state/state/bridge-data';

import { type NativeEmbedFeatureList } from '@canvas/databases-bridge-api/types';
import { type RovoContentBridgeAPI, type client } from '@canvas/rovo-content-bridge-api';

import { setupClipboardEventForwarding } from './handlers/forward-clipboard-events';
import { setupPointerEventForwarding } from './handlers/forward-drag-events';
import { setupKeyboardEventForwarding } from './handlers/forward-keyboard-events';
import { setupWheelEventForwarding } from './handlers/forward-wheel-events';

type MapBridgeCommandsToLocalMethodsParams = {
  /**
   * The local and external client ids for each bridge client.
   */
  clients: {
    localClientId: ReturnType<typeof client>;
    externalClientId: ReturnType<typeof client>;
  };

  /**
   * A list of features with an enabled/disabled status to determine
   * which bridge commands are enabled.
   */
  featureList: NativeEmbedFeatureList;

  /**
   * A factory method to return the current bridge state used among commands.
   */
  getBridgeData: () => BridgeData;
};

/**
 * Allows for conditionally registering bridge events and their local handlers.
 * When a bridge event comes into the local client, a callback with local / domain logic
 * can be registered.
 *
 * @returns A cleanup function that removes all registered event listeners
 */
export function mapBridgeCommandsToLocalMethods(
  bridge: RovoContentBridgeAPI,
  { clients: { localClientId, externalClientId } }: MapBridgeCommandsToLocalMethodsParams
): () => void {
  const cleanupFunctions: (() => void)[] = [];

  const clipboardCleanup = setupClipboardEventForwarding({
    bridge,
    localClientId,
    externalClientId,
  });

  const pointerCleanup = setupPointerEventForwarding({
    bridge,
    localClientId,
    externalClientId,
  });

  const wheelCleanup = setupWheelEventForwarding({
    bridge,
    localClientId,
    externalClientId,
  });

  const keyboardCleanup = setupKeyboardEventForwarding({
    bridge,
    localClientId,
    externalClientId,
  });

  cleanupFunctions.push(clipboardCleanup, pointerCleanup, wheelCleanup, keyboardCleanup);

  // Return cleanup function that removes all registered event listeners
  return () => {
    cleanupFunctions.forEach((cleanup) => cleanup());
  };
}
